import {
  PortableTextProps,
  PortableTextReactComponents,
  PortableText as SanityPortableText,
} from '@portabletext/react';
import type { TypedObject } from '@portabletext/types';

import { Sanity$TextBlock, Sanity$Video } from '../../types/sanityTypes';
import BreakRenderer from './BreakRenderer';
import SanityLink from './SanityLink';
import UnorderedListItem from './UnorderedListItem';
import YouTubeVideo from './YouTubeVideo';

const DefaultComponents: PortableTextReactComponents = {
  hardBreak: () => <br />,
  unknownMark: ({ children }) => <span>{children}</span>,
  unknownBlockStyle: ({ children }) => <div>{children}</div>,
  unknownListItem: ({ children }) => <li>{children}</li>,
  unknownList: ({ children }) => <ul>{children}</ul>,
  unknownType: ({ children }) => <div>{children}</div>,
  marks: {
    strong: ({ children }) => (
      <strong className="font-semibold">{children}</strong>
    ),
    link: ({ children, value }) => (
      <SanityLink mark={value}> {children}</SanityLink>
    ),
    callToActionButton: ({ children, value }) => (
      <a
        href={value.link?.href}
        rel={value.link?.rel}
        className="mt-6 inline-block rounded-md bg-peach-60 px-6 py-3 text-white no-underline hover:bg-peach-70 active:bg-peach-80"
      >
        {children}
      </a>
    ),
  },
  block: {
    h1: ({ children }) => (
      <h2 className="mb-6 text-2xl font-semibold text-balance break-words text-heading md:text-3xl lg:mr-16 lg:text-4xl">
        {children}
      </h2>
    ),
    h2: ({ children }) => (
      <h2 className="mb-6 text-2xl font-semibold text-balance break-words text-heading md:text-3xl lg:mr-16 lg:text-4xl">
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 className="mb-3 text-2xl font-medium break-words text-heading md:mb-6">
        {children}
      </h3>
    ),
    h4: ({ children }) => (
      <h4 className="mb-3 text-xl font-medium break-words text-heading md:mb-6">
        {children}
      </h4>
    ),
    h5: ({ children }) => (
      <h5 className="mb-3 text-lg font-medium break-words text-heading md:mb-6">
        {children}
      </h5>
    ),
    blockquote: ({ children }) => (
      <blockquote className="text-lg text-body italic">{children}</blockquote>
    ),
    normal: ({ children }) => <p className="text-base text-body">{children}</p>,
  },
  list: {
    bullet: ({ children }) => (
      <ul className="my-4 space-y-2 text-body">{children}</ul>
    ),
    number: ({ children }) => (
      <ol className="my-4 list-decimal space-y-2 pl-4 text-body">{children}</ol>
    ),
  },
  listItem: {
    bullet: ({ children }) => <UnorderedListItem>{children}</UnorderedListItem>,
    number: ({ children }) => <li className="pl-2 text-balance">{children}</li>,
  },
  types: {
    video: ({ value: sanityVideo }: { value: Sanity$Video }) => (
      <YouTubeVideo sanityVideo={sanityVideo} />
    ),
    image: () => null,
    typeformWidget: () => null,
    contentBreak: ({ value }) => <BreakRenderer node={value} />,
  },
};

/**
 * A wrapperaround Sanity's PortableText component that set defaults and allows you to override certain components on a case-by-case basis.
 * This wrapper provides deep merging of component overrides while preserving the default styling and behavior.
 *
 * @template B - The type of the portable text blocks
 * @param value - The portable text content to render
 * @param components - Component overrides to customize specific elements
 * @param props - Additional props passed to the underlying PortableText component
 *
 * @example
 * ```tsx
 * <PortableText
 *   value={content}
 *   components={{
 *     block: {
 *       h1: ({ children }) => <h1 className="custom-heading">{children}</h1>
 *     }
 *   }}
 * />
 * ```
 */

const PortableText = <B extends TypedObject = any>({
  value,
  components: componentOverrides,
  ...props
}: PortableTextProps<B>) => {
  return (
    <SanityPortableText
      value={value}
      components={
        {
          ...DefaultComponents,
          ...componentOverrides,
          // Deep merge nested objects
          ...(componentOverrides?.block && {
            block: {
              ...DefaultComponents.block,
              ...componentOverrides.block,
            },
          }),
          ...(componentOverrides?.marks && {
            marks: {
              ...DefaultComponents.marks,
              ...componentOverrides.marks,
            },
          }),
          ...(componentOverrides?.list && {
            list: {
              ...DefaultComponents.list,
              ...componentOverrides.list,
            },
          }),
          ...(componentOverrides?.listItem && {
            listItem: {
              ...DefaultComponents.listItem,
              ...componentOverrides.listItem,
            },
          }),
          ...(componentOverrides?.types && {
            types: {
              ...DefaultComponents.types,
              ...componentOverrides.types,
            },
          }),
        } as PortableTextReactComponents
      }
      {...props}
    />
  );
};

type PortablePTagProps = {
  value: Sanity$TextBlock | Sanity$TextBlock[];
  className: string;
};

/**
 * A shorthand component for applying specific CSS classes to a block of portable text.
 * This component is useful when you need to quickly style text content without custom component overrides.
 *
 * @param value - The Sanity text block(s) to render
 * @param className - The CSS class name(s) to apply to the paragraph elements
 *
 * @example
 * ```tsx
 * <PortablePTag
 *   value={textContent}
 *   className="text-lg text-gray-700 leading-relaxed"
 * />
 * ```
 */

export const PortablePTag = ({ value, className }: PortablePTagProps) => (
  <PortableText
    value={value}
    components={{
      block: {
        normal: ({ children }) => <p className={className}>{children}</p>,
      },
    }}
  />
);

export default PortableText;
